import { AlertTriangle } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';

/**
 * Drop-in replacement for window.confirm / window.alert / window.prompt
 * that renders as a centered, mobile-friendly modal. Native dialogs are
 * tiny on iPad/iPhone and easy to miss; this one is sized, scrollable,
 * focus-trapped lite, and dismissable by tapping the backdrop.
 *
 * Usage:
 *
 *   const { confirm, alert, prompt, dialog } = useDialog();
 *   ...
 *   const ok = await confirm('Delete this document?');
 *   if (!ok) return;
 *   ...
 *   // render the dialog somewhere in the page
 *   return <>{dialog} ... </>
 */

type DialogKind = 'confirm' | 'alert' | 'prompt';

interface DialogState {
  kind: DialogKind;
  message: string;
  resolve: (value: unknown) => void;
}

interface UseDialogResult {
  confirm: (message: string) => Promise<boolean>;
  alert: (message: string) => Promise<void>;
  prompt: (message: string, defaultValue?: string) => Promise<string | null>;
  dialog: ReactNode;
}

export function useDialog(): UseDialogResult {
  const [state, setState] = useState<DialogState | null>(null);
  const [inputValue, setInputValue] = useState('');

  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({ kind: 'confirm', message, resolve: (v) => resolve(Boolean(v)) });
    });
  }, []);

  const alert = useCallback((message: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      setState({ kind: 'alert', message, resolve: () => resolve() });
    });
  }, []);

  const prompt = useCallback((message: string, defaultValue = ''): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      setInputValue(defaultValue);
      setState({
        kind: 'prompt',
        message,
        resolve: (v) => resolve(v === null ? null : String(v)),
      });
    });
  }, []);

  function close(value: unknown): void {
    if (!state) return;
    state.resolve(value);
    setState(null);
    setInputValue('');
  }

  function onCancel(): void {
    if (!state) return;
    if (state.kind === 'confirm') close(false);
    else if (state.kind === 'prompt') close(null);
    else close(null);
  }

  function onOk(): void {
    if (!state) return;
    if (state.kind === 'confirm') close(true);
    else if (state.kind === 'prompt') close(inputValue);
    else close(null);
  }

  // Focus the primary button (or the prompt input) once the dialog renders.
  // Using a ref + useEffect rather than the autoFocus prop to keep
  // jsx-a11y/no-autofocus happy.
  function setAutoFocus(node: HTMLButtonElement | HTMLInputElement | null): void {
    if (node) node.focus();
  }

  const dialog = state ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sh-dialog-message"
      className="fixed inset-0 z-[1000] flex items-end justify-center bg-brand-black/55 p-4 backdrop-blur-sm sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-brand-white p-6 shadow-card-hover">
        <div className="flex items-start gap-3">
          <span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal"
            aria-hidden="true"
          >
            <AlertTriangle size={18} strokeWidth={2} />
          </span>
          <p
            id="sh-dialog-message"
            className="flex-1 pt-1 text-sm leading-relaxed text-brand-body-dark"
          >
            {state.message}
          </p>
        </div>

        {state.kind === 'prompt' ? (
          <input
            ref={setAutoFocus}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onOk();
              if (e.key === 'Escape') onCancel();
            }}
            className="mt-4 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
          />
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {state.kind !== 'alert' ? (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-11 items-center justify-center rounded-md border border-brand-surface bg-brand-white px-5 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40"
            >
              Cancel
            </button>
          ) : null}
          <button
            ref={(node) => {
              if (state.kind !== 'prompt') setAutoFocus(node);
            }}
            type="button"
            onClick={onOk}
            className="inline-flex h-11 items-center justify-center rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal hover:bg-brand-yellow-deep"
          >
            {state.kind === 'alert' ? 'OK' : state.kind === 'prompt' ? 'Save' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, alert, prompt, dialog };
}
